import React, { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple, FileText, Crown } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { formatRelativeTime } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

const METRIC_OPTIONS = [
  { id: "spend", label: "Spend" },
  { id: "impressions", label: "Impressions" },
  { id: "clicks", label: "Clicks" },
  { id: "ctr", label: "CTR" },
  { id: "cpc", label: "CPC" },
  { id: "cpl", label: "CPL" },
  { id: "roas", label: "ROAS" },
  { id: "conversions", label: "Conversions" },
];

export default function Reports() {
  const { user } = useAuth();
  const isAgency = user?.tier === "agency";
  const [title, setTitle] = useState("Weekly Performance Report");
  const [metrics, setMetrics] = useState(["spend", "ctr", "cpc", "roas"]);
  const [timeframe, setTimeframe] = useState("30d");
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [includeComparison, setIncludeComparison] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);

  const fetchReports = useCallback(async () => {
    const res = await api.get("/reports");
    setReports(res.data);
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const toggleMetric = (id) => {
    setMetrics((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const handleGenerate = async () => {
    if (metrics.length === 0) {
      toast.error("Select at least one metric");
      return;
    }
    setGenerating(true);
    try {
      await api.post("/reports/generate", { title, metrics, timeframe, white_label: whiteLabel && isAgency, include_platform_comparison: includeComparison });
      toast.success("Report generated");
      fetchReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      const res = await api.get(`/reports/${report.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download report");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
          <div>
            <h1 className="font-heading font-semibold text-2xl tracking-tight">Reports</h1>
            <p className="text-sm text-muted-foreground mt-1">Generate downloadable performance reports for your clients</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Build a Report</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="report-title">Report title</Label>
                <Input id="report-title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="report-title-input" />
              </div>

              <div className="space-y-1.5">
                <Label>Metrics to include</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {METRIC_OPTIONS.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-metric-${m.id}`}>
                      <Checkbox checked={metrics.includes(m.id)} onCheckedChange={() => toggleMetric(m.id)} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Timeframe</Label>
                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="w-52" data-testid="report-timeframe-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="report-white-label-toggle">
                <Checkbox checked={whiteLabel} onCheckedChange={setWhiteLabel} disabled={!isAgency} />
                <span className={!isAgency ? "text-muted-foreground" : ""}>White-label with my agency logo</span>
                {!isAgency && <Badge variant="outline" className="ml-1"><Crown className="h-3 w-3 mr-1" />Agency only</Badge>}
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="report-include-comparison-toggle">
                <Checkbox checked={includeComparison} onCheckedChange={setIncludeComparison} />
                <span>Include Google Ads vs Meta Ads platform comparison</span>
              </label>

              <Button onClick={handleGenerate} disabled={generating} data-testid="generate-report-button">
                <FileText className="h-4 w-4 mr-2" />
                {generating ? "Generating..." : "Generate PDF Report"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Report History</CardTitle>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No reports generated yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Timeframe</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((r) => (
                      <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                        <TableCell className="font-medium">{r.title}</TableCell>
                        <TableCell className="uppercase text-xs">{r.timeframe}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatRelativeTime(r.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => handleDownload(r)} data-testid={`download-report-${r.id}`}>
                            <DownloadSimple className="h-3.5 w-3.5 mr-1.5" /> Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
